import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useSettingsUsers, useUpdateUserRole } from "../../hooks/use-settings";

type Role = "ADMIN" | "VALUER" | "VIEWER";

export function UsersPage(): React.ReactElement {
  const { data, isLoading, error } = useSettingsUsers();
  const updateRoleMutation = useUpdateUserRole();

  const users = data?.users ?? [];

  const handleRoleChange = (id: string, role: Role): void => {
    updateRoleMutation.mutate(
      { id, role },
      {
        onSuccess: () => toast.success("Role updated"),
        onError: () => toast.error("Failed to update role"),
      },
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-semibold">Users</h2>
          <p className="text-sm text-muted-foreground">
            Manage user accounts and roles. Users authenticate via Microsoft Entra ID.
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" data-testid="users-loading">
          Loading…
        </p>
      ) : error ? (
        <p className="text-sm text-destructive" data-testid="users-error">
          Failed to load users.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="w-32">Role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  Users will appear here after first login via Entra ID.
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <select
                      value={u.role}
                      onChange={(e) => handleRoleChange(u.id, e.target.value as Role)}
                      disabled={updateRoleMutation.isPending}
                      className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid={`user-role-${u.id}`}
                    >
                      <option value="ADMIN">Admin</option>
                      <option value="VALUER">Valuer</option>
                      <option value="VIEWER">View Only</option>
                    </select>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
